import React, { useEffect, useMemo, useRef, useState } from "react";
import { useInViewOnce } from "../utils/useInViewOnce";
import "./BarChart.scss";

interface BarDatum {
  type: string;
  avg: number;
}

interface BarChartProps {
  data: BarDatum[];
  maxAvg: number;
  width?: number;
  height?: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  leftMargin?: number;
  colors: string[];
  barLabel?: (d: BarDatum, i: number) => string;
  className?: string;
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  maxAvg,
  width = 800,
  height = 220,
  margin = { top: 20, right: 40, bottom: 40, left: 450 },
  leftMargin,
  colors,
  barLabel,
  className,
}) => {
  const { ref, inView } = useInViewOnce<SVGSVGElement>();
  const [progress, setProgress] = useState(0);
  const [showValues, setShowValues] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const durationMs = 800;

  // Determine responsiveness from className (presence of 'responsive-chart')
  const isResponsive =
    !!className && className.split(" ").includes("responsive-chart");

  // Responsive font sizes are handled via CSS classes (y-axis/value/x-axis)

  useEffect(() => {
    if (!inView) return;
    // animate once when in view
    const step = (ts: number) => {
      if (startTimeRef.current === null) startTimeRef.current = ts;
      const elapsed = ts - startTimeRef.current;
      const t = Math.min(1, elapsed / durationMs);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setProgress(eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [inView]);

  useEffect(() => {
    if (progress >= 0.999) {
      // allow next paint then fade in values
      const id = requestAnimationFrame(() => setShowValues(true));
      return () => cancelAnimationFrame(id);
    }
  }, [progress]);

  // Use leftMargin prop if provided, otherwise use margin.left
  const effectiveLeftMargin = leftMargin || margin.left;
  const effectiveMargin = { ...margin, left: effectiveLeftMargin };

  const innerWidth = width - effectiveMargin.left - effectiveMargin.right;
  const innerHeight = height - effectiveMargin.top - effectiveMargin.bottom;

  const animatedData = useMemo(() => {
    const scale = progress;
    return data.map((d) => ({ ...d, avg: d.avg * scale }));
  }, [data, progress]);

  return (
    <svg
      ref={ref}
      {...(isResponsive ? {} : { width, height })}
      viewBox={`0 0 ${width} ${height}`}
      style={{ marginBottom: 24, overflow: "visible" }}
      className={className}
    >
      <g
        transform={`translate(${effectiveMargin.left},${effectiveMargin.top})`}
      >
        {/* Bars */}
        {animatedData.map((d, i) => (
          <rect
            key={d.type}
            x={0}
            y={i * (innerHeight / data.length)}
            width={(d.avg / maxAvg) * innerWidth}
            height={innerHeight / data.length - 6}
            fill={colors[i % colors.length]}
          />
        ))}
        {/* Labels */}
        {data.map((d, i) => {
          const centerY =
            i * (innerHeight / data.length) + innerHeight / data.length / 2;

          if (d.type === "Increased fundraising efforts") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Increased
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  fundraising efforts
                </text>
              </g>
            );
          }

          if (d.type === "Streamlined services to reduce costs") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Streamlined services to
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  reduce costs
                </text>
              </g>
            );
          }

          // Handle multi-line labels for TrendingTechSection
          if (d.type === "Actively exploring potential applications") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Actively exploring potential
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  applications
                </text>
              </g>
            );
          }

          if (d.type === "Data analysis or reporting") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Data analysis or
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  reporting
                </text>
              </g>
            );
          }

          if (d.type === "Intentionally avoiding") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Intentionally
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  avoiding
                </text>
              </g>
            );
          }

          if (d.type === "Virtual service delivery tools (Zoom/Teams)") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Virtual service delivery tools
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  (Zoom/Teams)
                </text>
              </g>
            );
          }

          if (d.type === "Case-management platforms") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Case-management
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  platforms
                </text>
              </g>
            );
          }

          if (d.type === "Learning management systems") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Learning management
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  systems
                </text>
              </g>
            );
          }

          if (d.type === "No new technology") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  No new
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  technology
                </text>
              </g>
            );
          }

          if (d.type === "Data dashboards") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Data
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  dashboards
                </text>
              </g>
            );
          }

          if (d.type === "Limited staff capacity/expertise") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Limited staff capacity/
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  expertise
                </text>
              </g>
            );
          }

          if (d.type === "Technology infrastructure limitations") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Technology infrastructure
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  limitations
                </text>
              </g>
            );
          }

          if (d.type === "Data privacy/security concerns") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Data privacy/security
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  concerns
                </text>
              </g>
            );
          }

          // RespondentsOverviewSection labels
          if (d.type === "National Nonprofit Service Provider") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  National Nonprofit
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Service Provider
                </text>
              </g>
            );
          }

          if (d.type === "Academic/Research Institution") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Academic/Research
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Institution
                </text>
              </g>
            );
          }

          if (d.type === "Workforce Development/Job Training") {
            return (
              <g key={d.type + "-label"}>
                <text
                  x={-8}
                  y={centerY - 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Workforce Development/
                </text>
                <text
                  x={-8}
                  y={centerY + 6}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  className="y-axis-text"
                  fontWeight={500}
                >
                  Job Training
                </text>
              </g>
            );
          }

          // Default single line for other labels
          return (
            <text
              key={d.type + "-label"}
              x={-8}
              y={centerY}
              textAnchor="end"
              alignmentBaseline="middle"
              className="y-axis-text"
              fontWeight={500}
            >
              {d.type}
            </text>
          );
        })}
        {/* Values (fade in once animation completes) */}
        <g
          style={{
            opacity: showValues ? 1 : 0,
            transition: "opacity 300ms ease",
          }}
        >
          {data.map((d, i) => (
            <text
              key={d.type + "-value"}
              x={(d.avg / maxAvg) * innerWidth + 8}
              y={
                i * (innerHeight / data.length) + innerHeight / data.length / 2
              }
              alignmentBaseline="middle"
              className="value-text"
              fill="#333"
            >
              {barLabel ? barLabel(d, i) : Math.round(d.avg).toString()}
            </text>
          ))}
        </g>
        {/* X Axis */}
        {(() => {
          // Use maxAvg for consistent scaling and create ticks in increments of 10
          const tickValues = [];
          const increment = 10;

          for (let value = 0; value <= maxAvg; value += increment) {
            tickValues.push(value);
          }

          return tickValues.map((value, i) => (
            <g key={i}>
              <line
                x1={(value / maxAvg) * innerWidth}
                x2={(value / maxAvg) * innerWidth}
                y1={0}
                y2={innerHeight}
                stroke="#ccc"
                strokeDasharray="2,2"
              />
              <text
                className="x-axis x-axis-text"
                x={(value / maxAvg) * innerWidth}
                y={innerHeight + 18}
                textAnchor="middle"
                fill="#888"
              >
                {value}
              </text>
            </g>
          ));
        })()}
      </g>
    </svg>
  );
};

export default BarChart;
